import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { 
  MessageCircle, 
  Phone,
  QrCode,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Loader2,
  Send,
  Copy,
  Check
} from 'lucide-react';

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram icon component
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export function ChannelsPage() {
  const [channelStatus, setChannelStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [telegramToken, setTelegramToken] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/telegram`;

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.getChannelsStatus();
      setChannelStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch channel status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const fetchQRCode = async () => {
    setQrLoading(true);
    try {
      const response = await api.getWhatsAppQR();
      setQrCode(response.data.qr);
    } catch (err) {
      console.error('Failed to fetch QR code:', err);
    } finally {
      setQrLoading(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const ConnectionStatus = ({ connected }) => (
    <Badge 
      variant={connected ? "default" : "secondary"}
      className={connected ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""}
    >
      {connected ? (
        <>
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Connected
        </>
      ) : (
        <>
          <XCircle className="h-3 w-3 mr-1" />
          Disconnected
        </>
      )}
    </Badge>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-10 w-64 bg-secondary rounded" />
          <div className="grid grid-cols-2 gap-6">
            <div className="h-96 bg-secondary rounded" />
            <div className="h-96 bg-secondary rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="channels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            COMMUNICATION CHANNELS
          </h1>
          <p className="text-muted-foreground mt-1">
            Connect WhatsApp and Telegram to enable multi-channel inference
          </p>
        </div>
        <Button variant="outline" onClick={fetchStatus} data-testid="refresh-status-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* WhatsApp Card */}
        <Card className="border-border bg-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <WhatsAppIcon className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <CardTitle className="text-lg">WhatsApp</CardTitle>
                  <CardDescription className="text-xs">QR Code Pairing</CardDescription>
                </div>
              </div>
              <ConnectionStatus connected={channelStatus?.whatsapp?.connected} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {channelStatus?.whatsapp?.connected ? (
              <div className="text-center py-8">
                <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium">WhatsApp Connected</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Messages will be routed through the tiered inference system
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  {qrCode ? (
                    <div className="inline-block p-4 bg-white rounded-lg">
                      <img 
                        src={qrCode} 
                        alt="WhatsApp QR Code" 
                        className="w-48 h-48"
                        data-testid="whatsapp-qr-code"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-48 mx-auto bg-secondary rounded-lg flex items-center justify-center">
                      <QrCode className="h-16 w-16 text-muted-foreground" />
                    </div>
                  )}
                </div>
                
                <div className="text-center">
                  <Button 
                    onClick={fetchQRCode} 
                    disabled={qrLoading}
                    data-testid="get-qr-btn"
                  >
                    {qrLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <QrCode className="h-4 w-4 mr-2" />
                    )}
                    {qrCode ? 'Refresh QR Code' : 'Get QR Code'}
                  </Button>
                </div>

                <Separator />

                <div className="text-sm text-muted-foreground space-y-2">
                  <p className="font-medium text-foreground">How to connect:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Open WhatsApp on your phone</li>
                    <li>Go to Settings → Linked Devices</li>
                    <li>Tap "Link a Device"</li>
                    <li>Scan the QR code above</li>
                  </ol>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Telegram Card */}
        <Card className="border-border bg-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <TelegramIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-lg">Telegram</CardTitle>
                  <CardDescription className="text-xs">Bot API Webhook</CardDescription>
                </div>
              </div>
              <ConnectionStatus connected={channelStatus?.telegram?.connected} />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {channelStatus?.telegram?.connected ? (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <CheckCircle2 className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium">Telegram Bot Connected</h3>
                  {channelStatus?.telegram?.bot_username && (
                    <p className="text-sm text-muted-foreground mt-1">
                      @{channelStatus.telegram.bot_username}
                    </p>
                  )}
                </div>
                
                <Separator />
                
                <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                  <p className="text-xs text-muted-foreground mb-2">Webhook URL</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs font-mono bg-background px-2 py-1 rounded overflow-x-auto">
                      {webhookUrl}
                    </code>
                    <Button size="sm" variant="ghost" onClick={copyWebhookUrl}>
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground space-y-3">
                  <p className="font-medium text-foreground">Setup Instructions:</p>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>
                      Message{' '}
                      <a 
                        href="https://t.me/botfather" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        @BotFather
                      </a>
                      {' '}on Telegram
                    </li>
                    <li>Send <code className="bg-secondary px-1 rounded">/newbot</code> and follow instructions</li>
                    <li>Copy your bot token</li>
                    <li>Add token to backend environment</li>
                    <li>Set webhook URL (below)</li>
                  </ol>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-sm">Webhook URL (for BotFather)</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={webhookUrl}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button size="sm" variant="outline" onClick={copyWebhookUrl}>
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Set this URL using: <code className="bg-secondary px-1 rounded">/setwebhook</code>
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-400">
                    <strong>Note:</strong> Add <code>TELEGRAM_BOT_TOKEN</code> to backend .env file 
                    and restart the service.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-sm font-medium">How Channel Integration Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <h4 className="text-sm font-medium mb-1">1. Receive</h4>
              <p className="text-xs text-muted-foreground">
                Messages arrive via WhatsApp or Telegram
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-muted-foreground" />
              </div>
              <h4 className="text-sm font-medium mb-1">2. Route</h4>
              <p className="text-xs text-muted-foreground">
                Tiered router selects optimal model
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 mx-auto mb-3 flex items-center justify-center">
                <span className="text-primary font-bold">AI</span>
              </div>
              <h4 className="text-sm font-medium mb-1">3. Process</h4>
              <p className="text-xs text-muted-foreground">
                LLM generates intelligent response
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                <Send className="h-6 w-6 text-muted-foreground" />
              </div>
              <h4 className="text-sm font-medium mb-1">4. Reply</h4>
              <p className="text-xs text-muted-foreground">
                Response sent back to user
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ChannelsPage;
